<template>
  <footer class="bg-green-700 text-white py-12">
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        <!-- Brand Section -->
        <div class="space-y-6 group">
          <router-link to="/" class="flex items-center">
            <img
              :src="filmaLogo"
              alt="Filma Logo"
              class="h-10 w-10 mr-2 text-white transition-transform duration-500 transform hover:rotate-12 hover:scale-110"
            />
            <span class="text-3xl font-bold">FILMA</span>
          </router-link>
          <p class="text-white leading-relaxed mt-4">
            Empowering farmers with innovative technology, data-driven insights,
            and sustainable agricultural solutions.
          </p>
        </div>

        <!-- Our Portals -->
        <div class="space-y-6">
          <h3 class="text-xl font-semibold border-b-2 border-white pb-2">
            <i class="fas fa-th-large mr-2"></i> Our Portals
          </h3>
          <ul class="space-y-3">
            <li v-for="(portal, index) in portals" :key="index">
              <a
                :href="portal.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-white hover:text-green-200 hover:pl-2 transition-all duration-300 inline-block items-center"
              >
                <i :class="['fas', portal.icon, 'mr-2']"></i>
                {{ portal.name }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Newsletter -->
        <div class="space-y-6">
          <h3 class="text-xl font-semibold border-b-2 border-white pb-2">
            <i class="fas fa-envelope-open-text mr-2"></i> Stay Connected
          </h3>
          <p class="text-white mb-4">
            Subscribe to receive insights, market trends, and agricultural
            updates.
          </p>
          <form @submit.prevent="subscribeNewsletter" class="space-y-4">
            <div class="relative">
              <input
                type="email"
                v-model="email"
                placeholder="Enter your email"
                required
                :disabled="isSubmitting"
                class="w-full px-4 py-3 bg-green-800 border border-green-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-white transition-all duration-300"
              />
              <i
                class="fas fa-at absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300"
              ></i>
            </div>
            <button
              type="submit"
              :disabled="isSubmitting"
              class="w-full px-6 py-3 bg-white text-green-700 hover:bg-green-100 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
              :class="{ 'animate-button-submit': isSubmitting }"
            >
              <i class="fas fa-paper-plane text-green-700 mr-2"></i>
              <span>{{ isSubmitting ? "Subscribing..." : "Subscribe" }}</span>
            </button>
          </form>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="mt-12 pt-6 border-t border-green-600 text-center">
        <p class="text-white text-sm">
          © {{ currentYear }} FILMA. All rights reserved.
        </p>
      </div>
    </div>

    <!-- Success Modal -->
    <div
      v-if="showSuccessModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm animate-slideDown"
    >
      <div
        class="bg-white p-12 rounded-3xl shadow-2xl text-center max-w-md transform"
      >
        <div class="mb-6">
          <i
            class="fas fa-check-circle h-24 w-24 mx-auto text-green-500 text-6xl animate-bounce"
          ></i>
        </div>
        <h2
          class="text-3xl font-bold text-slate-800 mb-4 opacity-0 animate-fade-in-delay"
        >
          Successfully Subscribed!
        </h2>
        <p class="text-slate-600 mb-6 opacity-0 animate-fade-in-delay-2">
          Thank you for subscribing to FILMA. You'll receive our latest insights
          soon!
        </p>
        <button
          @click="closeModal"
          class="bg-green-700 hover:bg-green-800 text-white px-8 py-3 rounded-xl transform transition-all duration-300 hover:scale-[1.02] opacity-0 animate-fade-in-delay-3"
        >
          Close
        </button>
      </div>
    </div>
  </footer>
</template>

<script>
import filmaLogo from "@/assets/images/filma-logo.svg";

export default {
  name: "AppFooter",
  data() {
    return {
      filmaLogo,
      email: "",
      isSubmitting: false,
      showSuccessModal: false,
      currentYear: new Date().getFullYear(),
      portals: [
        {
          name: "Partners Portal",
          url: "https://partners.lastmilefinance.app",
          icon: "fa-handshake",
        },
        {
          name: "VSLA Portal",
          url: "https://vsla.lastmilefinance.app",
          icon: "fa-users",
        },
        {
          name: "Institutions Portal",
          url: "https://institutions.lastmilefinance.app",
          icon: "fa-building",
        },
      ],
    };
  },
  methods: {
    async subscribeNewsletter() {
      if (!this.email || this.isSubmitting) return;

      this.isSubmitting = true;

      try {
        const response = await fetch(
          "https://formspree.io/f/xpwqkqgladfdfasdfasfd",
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: this.email,
              type: "newsletter_subscription",
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Subscription failed");
        }

        // Reset form and show success modal
        this.email = "";
        this.showSuccessModal = true;
      } catch (error) {
        // You might want to show an error modal here
      } finally {
        this.isSubmitting = false;
      }
    },
    closeModal() {
      this.showSuccessModal = false;
    },
  },
};
</script>

<style scoped>
.animate-button-submit {
  animation: buttonSubmit 0.5s infinite alternate;
}

.animate-slideDown {
  animation: slideDown 0.3s ease-in-out forwards;
}

.animate-fade-in-delay {
  animation: fadeIn 0.8s ease-out 0.2s forwards;
}

.animate-fade-in-delay-2 {
  animation: fadeIn 0.8s ease-out 0.4s forwards;
}

.animate-fade-in-delay-3 {
  animation: fadeIn 0.8s ease-out 0.6s forwards;
}

@keyframes buttonSubmit {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.05);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
